import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useLazyQuery, useQuery } from '@apollo/client/react';
import {
  Avatar,
  type InputProps,
  ListItem,
  ListItemAvatar,
  ListItemText,
  type SxProps,
  type Theme,
  debounce,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { LocateFixedIcon, TargetIcon } from 'lucide-react';

import { gql } from '@graphql/__generated__';
import {
  type LeadSelectFragment,
  type Leads_Bool_Exp,
  type Leads_Order_By,
  Order_By,
} from '@graphql/__generated__/graphql';
import { RaAutoComplete } from '@modules/_shared/components/data-grid/RaAutoComplete';
import { SelectStartIcon } from '@modules/_shared/components/form/SelectStartIcon';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { formatAddress } from '@modules/_shared/utils/formatting';
import { userCanViewLeadDetails } from '@modules/_shared/utils/listingPermissions';
import { useLocale } from '@shared/hooks/locale';

export const LEAD_SELECT_FRAGMENT = gql(/* GraphQL */ `
  fragment LeadSelect on leads {
    id
    anonymized_contact
    created_by
    origin_enquiry_id
    property {
      route
      street_number
      postcode
      locality
    }
    contact {
      id
      first_name
      last_name
    }
    lead_agents {
      user_id
      claimed_at
      source
    }
  }
`);

export const SEARCH_LEADS_TSV = gql(/* GraphQL */ `
  query SearchLeadsTsv(
    $q: String!
    $where: leads_bool_exp!
    $orderBy: [leads_order_by!]
  ) {
    search_leads_tsv(
      args: { search_text: $q }
      limit: 10
      where: $where
      order_by: $orderBy
    ) {
      id
      ...LeadSelect
    }
  }
`);

const SEARCH_LEADS = gql(/* GraphQL */ `
  query SearchLeads($where: leads_bool_exp!, $orderBy: [leads_order_by!]) {
    leads(where: $where, limit: 10, order_by: $orderBy) {
      id
      ...LeadSelect
    }
  }
`);

const GET_DEFAULT_LEAD = gql(/* GraphQL */ `
  query GetDefaultLead($id: uuid!) {
    leads_by_pk(id: $id) {
      id
      ...LeadSelect
    }
  }
`);

type LeadSelectProps = {
  leadId?: string;
  onChange?: (lead: LeadSelectFragment | null) => void;
  placeholder?: string;
  InputProps?: InputProps;
  sx?: SxProps<Theme>;
  autoFocus?: boolean;
  customWhereClause?: (searchValue?: string) => Leads_Bool_Exp;
  instantQuery?: boolean;
  orderBy?: Leads_Order_By;
  disabled?: boolean;
};

const getContactName = ({
  firstName,
  lastName,
}: {
  firstName?: string | null;
  lastName?: string | null;
}) => {
  if (firstName && lastName) {
    return `${firstName} ${lastName}`;
  }
  return firstName ?? lastName ?? null;
};

export const LeadSelect = ({
  onChange,
  placeholder,
  InputProps,
  leadId,
  autoFocus = false,
  sx,
  customWhereClause,
  instantQuery = false,
  orderBy = { created_at: Order_By.Desc },
  disabled,
}: LeadSelectProps) => {
  const { t } = useLocale();
  const { me } = useAppData();
  const { breakpoints } = useTheme();
  const isMobile = useMediaQuery(breakpoints.down('sm'));
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<LeadSelectFragment[]>([]);
  const [selectedValue, setSelectedValue] = useState<LeadSelectFragment | null>(
    null,
  );

  const { data: defaultLead } = useQuery(GET_DEFAULT_LEAD, {
    skip: !leadId,
    variables: { id: leadId ?? '' },
  });

  useEffect(() => {
    if (defaultLead?.leads_by_pk != null) {
      setSelectedValue(defaultLead.leads_by_pk);
    }
  }, [defaultLead]);

  const [searchLeadsTsv, { loading: loadingTsv, error: errorTsv }] =
    useLazyQuery(SEARCH_LEADS_TSV, { notifyOnNetworkStatusChange: true });
  const [searchLeads, { loading: loadingLeads, error: errorLeads }] =
    useLazyQuery(SEARCH_LEADS, { notifyOnNetworkStatusChange: true });

  const handleSearch = useCallback(
    async (value?: string) => {
      if (selectedValue != null) {
        return;
      }

      const shouldSearch = instantQuery || (value != null && value.length >= 3);

      if (!shouldSearch) {
        return;
      }

      const variables = {
        where: {
          completed: { _eq: true },
          ...(customWhereClause ? customWhereClause(value) : {}),
        },
        orderBy,
      };

      if (value && value.length >= 1) {
        const response = await searchLeadsTsv({
          variables: { ...variables, q: value },
        });
        setOpen(true);
        setOptions(response.data?.search_leads_tsv ?? []);
      } else {
        const response = await searchLeads({ variables });
        setOpen(true);
        setOptions(response.data?.leads ?? []);
      }
    },
    [
      instantQuery,
      selectedValue,
      customWhereClause,
      orderBy,
      searchLeadsTsv,
      searchLeads,
    ],
  );

  const debouncedSearch = useMemo(
    () => debounce(handleSearch, 275),
    [handleSearch],
  );

  useEffect(() => {
    if (instantQuery) {
      handleSearch();
    }
  }, [instantQuery, handleSearch]);

  const handleChange = useCallback(
    (_event: any, value: LeadSelectFragment | null) => {
      setSelectedValue(value);
      onChange?.(value);

      if (isMobile && inputRef.current) {
        inputRef.current.blur();
      }
    },
    [onChange, isMobile],
  );

  const renderOption = useCallback(
    (props: any, option: LeadSelectFragment) => {
      const canViewLeadDetails = userCanViewLeadDetails(me, option);

      return (
        <ListItem
          {...props}
          key={option.id}
          sx={{
            padding: '1px 10px',
          }}
        >
          <ListItemAvatar>
            <Avatar>
              <LocateFixedIcon size={18} />
            </Avatar>
          </ListItemAvatar>
          <ListItemText
            sx={{
              textOverflow: 'ellipsis',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
            }}
            primary={
              canViewLeadDetails
                ? getContactName({
                    firstName: option.contact?.first_name,
                    lastName: option.contact?.last_name,
                  })
                : option.anonymized_contact
            }
            secondary={formatAddress(
              {
                street_number: option.property?.street_number,
                route: option.property?.route,
                postcode: option.property?.postcode,
                locality: option.property?.locality,
              },
              ', ',
              true,
              { hideRoute: !canViewLeadDetails },
            )}
          />
        </ListItem>
      );
    },
    [me],
  );

  return (
    <RaAutoComplete<LeadSelectFragment>
      sx={sx}
      value={selectedValue}
      getOptionLabel={option => {
        const canViewLeadDetails = userCanViewLeadDetails(me, option);
        return `${
          canViewLeadDetails
            ? getContactName({
                firstName: option.contact?.first_name,
                lastName: option.contact?.last_name,
              })
            : option.anonymized_contact
        } ${
          option.property &&
          formatAddress(option.property, ', ', true, {
            hideRoute: !canViewLeadDetails,
          })
        }`;
      }}
      open={open}
      autoFocus={autoFocus}
      options={options}
      InputProps={{
        ...InputProps,
        inputRef,
        startAdornment: InputProps?.startAdornment ?? (
          <SelectStartIcon icon={TargetIcon} />
        ),
        placeholder:
          InputProps?.placeholder ?? placeholder ?? t('Search leads'),
        onFocus: () => {
          if (!selectedValue) {
            handleSearch();
          }
        },
        onBlur: () => {
          setOpen(false);
          setOptions([]);
        },
      }}
      disabled={disabled}
      loading={loadingTsv || loadingLeads}
      setOpen={setOpen}
      setOptions={setOptions}
      allowEmptySearch={instantQuery}
      debouncedSearch={debouncedSearch}
      renderOption={renderOption}
      onChange={handleChange}
      error={errorTsv != null || errorLeads != null}
    />
  );
};
